import styled, { keyframes } from 'styled-components';
import { X } from 'lucide-react';
import { getColorHex } from '../../utils/mapApiProduct';
import { PRICE_BUCKET_DEFS } from '../../utils/shopPlpQuery';
import { Button } from '../ui/Button';

const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0.92; }
  to { transform: translateX(0); opacity: 1; }
`;

const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
`;

const Backdrop = styled.button`
  position: absolute;
  inset: 0;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  background: hsla(20, 8%, 8%, 0.52);
  backdrop-filter: blur(6px);
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transition: opacity 0.35s ${({ theme }) => theme.easeLuxe};
`;

const Panel = styled.aside`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: min(100%, 22.5rem);
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    165deg,
    hsl(38 42% 97%) 0%,
    ${({ theme }) => theme.colors.background} 38%,
    hsl(35 28% 94%) 100%
  );
  box-shadow: 8px 0 40px hsla(20, 10%, 10%, 0.12);
  transform: translateX(${(p) => (p.$open ? '0' : '-100%')});
  transition: transform 0.48s ${({ theme }) => theme.easeLuxe};

  ${(p) =>
    p.$open
      ? `
    animation: ${slideIn} 0.48s ease;
  `
      : ''}
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.35rem 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.champagneDark};
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.15rem, 0.5vw + 1rem, 1.35rem);
  font-weight: 300;
  letter-spacing: 0.04em;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.foreground};
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.noir};
    background: ${({ theme }) => theme.colors.secondary};
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 1.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 2.25rem;
`;

const Section = styled.section``;

const SectionTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: clamp(10px, 0.14vw + 9px, 12px);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0.85rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.champagneDark};
    box-shadow: 0 2px 12px hsla(30, 20%, 40%, 0.06);
  }
`;

const ToggleLabel = styled.span`
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.06em;
`;

const ToggleInput = styled.input`
  width: 1.1rem;
  height: 1.1rem;
  accent-color: ${({ theme }) => theme.colors.terracottaDark};
`;

const PriceGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PriceOption = styled.button`
  text-align: left;
  padding: 0.75rem 1rem;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.noir : theme.colors.border)};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.noir : theme.colors.background};
  color: ${({ $active, theme }) => ($active ? theme.colors.cream : theme.colors.foreground)};
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.04em;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.22s ${({ theme }) => theme.easeLuxe};

  &:hover {
    border-color: ${({ theme }) => theme.colors.noir};
  }
`;

const ColorGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  max-height: min(240px, 40vh);
  overflow-y: auto;
  padding-right: 4px;
`;

const ColorChip = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 3.25rem;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
`;

const Swatch = styled.span`
  width: 2rem;
  height: 2rem;
  border-radius: 999px;
  background: ${({ $hex }) => $hex};
  box-shadow: inset 0 0 0 1px hsla(0, 0%, 0%, 0.08);
  outline: 2px solid transparent;
  outline-offset: 3px;
  transition: outline-color 0.2s ease, transform 0.2s ease;

  ${({ $active, theme }) =>
    $active ? `outline-color: ${theme.colors.terracottaDark}; transform: scale(1.06);` : ''}
`;

const ColorName = styled.span`
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: capitalize;
  color: ${({ theme }) => theme.colors.mutedForeground};
  max-width: 4.5rem;
  text-align: center;
  line-height: 1.2;
`;

const SizeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(2.75rem, 1fr));
  gap: 0.45rem;
`;

const SizeChip = styled.button`
  min-height: 2.5rem;
  padding: 0.35rem 0.25rem;
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.colors.noir : theme.colors.border)};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.noir : 'transparent'};
  color: ${({ $active, theme }) => ($active ? theme.colors.cream : theme.colors.foreground)};
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-weight: 500;
  letter-spacing: 0.08em;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.noir};
  }
`;

const Footer = styled.footer`
  padding: 1.25rem 1.5rem 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.champagneDark};
  background: hsla(38, 38%, 96%, 0.92);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const ClearLink = styled.button`
  border: none;
  background: none;
  padding: 0.35rem;
  font-size: clamp(10px, 0.14vw + 9px, 12px);
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedForeground};
  cursor: pointer;
  font-family: inherit;

  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-style: italic;
`;

export default function ShopFiltersPanel({
  open,
  onClose,
  titleLabel,
  closeAria,
  saleLabel,
  saleOn,
  onSaleChange,
  priceSectionLabel,
  tPriceLabel,
  activePriceBucketId,
  onPriceBucketChange,
  colorSectionLabel,
  colorOptions,
  selectedColors,
  onColorsChange,
  sizeSectionLabel,
  sizeOptions,
  selectedSizes,
  onSizesChange,
  applyLabel,
  clearLabel,
  onClear,
  showSaleToggle = true,
}) {
  const toggleColor = (name) => {
    const next = selectedColors.includes(name)
      ? selectedColors.filter((c) => c !== name)
      : [...selectedColors, name];
    onColorsChange(next);
  };

  const toggleSize = (size) => {
    const next = selectedSizes.includes(size)
      ? selectedSizes.filter((s) => s !== size)
      : [...selectedSizes, size];
    onSizesChange(next);
  };

  return (
    <Root $open={open} aria-hidden={!open}>
      <Backdrop type="button" $open={open} aria-label={closeAria} onClick={onClose} tabIndex={-1} />
      <Panel $open={open} role="dialog" aria-modal="true" aria-label={titleLabel}>
        <Header>
          <Title>{titleLabel}</Title>
          <CloseBtn type="button" aria-label={closeAria} onClick={onClose}>
            <X size={18} strokeWidth={1.75} />
          </CloseBtn>
        </Header>
        <Body>
          {showSaleToggle ? (
            <Section>
              <ToggleRow>
                <ToggleLabel>{saleLabel}</ToggleLabel>
                <ToggleInput
                  type="checkbox"
                  checked={saleOn}
                  onChange={(e) => onSaleChange(e.target.checked)}
                />
              </ToggleRow>
            </Section>
          ) : null}

          <Section>
            <SectionTitle>{priceSectionLabel}</SectionTitle>
            <PriceGrid>
              <PriceOption
                type="button"
                $active={!activePriceBucketId}
                onClick={() => onPriceBucketChange(null)}
              >
                {tPriceLabel('priceAny')}
              </PriceOption>
              {PRICE_BUCKET_DEFS.map((b) => (
                <PriceOption
                  key={b.id}
                  type="button"
                  $active={activePriceBucketId === b.id}
                  onClick={() =>
                    onPriceBucketChange(activePriceBucketId === b.id ? null : b.id)
                  }
                >
                  {tPriceLabel(b.labelKey)}
                </PriceOption>
              ))}
            </PriceGrid>
          </Section>

          <Section>
            <SectionTitle>{colorSectionLabel}</SectionTitle>
            {colorOptions.length === 0 ? (
              <EmptyHint>—</EmptyHint>
            ) : (
              <ColorGrid>
                {colorOptions.map((name) => (
                  <ColorChip key={name} type="button" onClick={() => toggleColor(name)}>
                    <Swatch $hex={getColorHex(name)} $active={selectedColors.includes(name)} />
                    <ColorName>{name}</ColorName>
                  </ColorChip>
                ))}
              </ColorGrid>
            )}
          </Section>

          <Section>
            <SectionTitle>{sizeSectionLabel}</SectionTitle>
            {sizeOptions.length === 0 ? (
              <EmptyHint>—</EmptyHint>
            ) : (
              <SizeGrid>
                {sizeOptions.map((size) => (
                  <SizeChip
                    key={size}
                    type="button"
                    $active={selectedSizes.includes(size)}
                    onClick={() => toggleSize(size)}
                  >
                    {size}
                  </SizeChip>
                ))}
              </SizeGrid>
            )}
          </Section>
        </Body>
        <Footer>
          <Button type="button" $variant="luxe" $size="lg" style={{ width: '100%' }} onClick={onClose}>
            {applyLabel}
          </Button>
          <ClearLink type="button" onClick={onClear}>
            {clearLabel}
          </ClearLink>
        </Footer>
      </Panel>
    </Root>
  );
}
